import type { IPluginRegistryRepository } from '@/domain/repositories/IPluginRegistryRepository';
import type { PluginRegistryEntry } from '@/domain/entities/PluginRegistryEntry';
import type { DeviceSession } from '@/domain/entities/DeviceSession';
import { DeviceSessionExpiredError } from '@/infrastructure/http/DeviceSessionExpiredError';
import { NetworkUnavailableError } from '@/infrastructure/http/NetworkUnavailableError';
import { PluginNotRegisteredError } from '@/infrastructure/http/PluginNotRegisteredError';

export type ResolvePluginRegistryResult =
  | { success: true; entry: PluginRegistryEntry }
  // CA-11: tipo sem entrada no registro — terminal, nunca retentado.
  | { success: false; reason: 'not-configured' }
  // CA-03 (HUB-235), mesmo mecanismo de `GET /events/:id/config`.
  | { success: false; reason: 'session-expired'; expiredAt: Date }
  // CA-16: falha de rede pura — retentável (mesma regra de CA-06/07/08).
  | { success: false; reason: 'network' }
  // Qualquer outra falha (schema inesperado, HTTP 5xx) — também retentável,
  // mas com mensagem final genérica em vez de "sem conexão" (CA-16 só
  // define o texto para o caso de rede).
  | { success: false; reason: 'other'; error: string };

/**
 * Use case responsável por resolver o registro de plugin do tipo de
 * ativação atual (HUB-237, CA-01/CA-11/CA-12, ver ADR-010). Encapsula o
 * tratamento de erro da chamada ao repositório, mantendo a camada de
 * apresentação livre de try/catch de infraestrutura — mesmo padrão de
 * `LoadEventConfigUseCase` (HUB-235).
 */
export class ResolvePluginRegistryUseCase {
  constructor(private readonly repository: IPluginRegistryRepository) {}

  async execute(
    session: DeviceSession,
    activationType: string
  ): Promise<ResolvePluginRegistryResult> {
    try {
      const entry = await this.repository.resolve(session, activationType);
      return { success: true, entry };
    } catch (error) {
      if (error instanceof PluginNotRegisteredError) {
        return { success: false, reason: 'not-configured' };
      }
      if (error instanceof DeviceSessionExpiredError) {
        return { success: false, reason: 'session-expired', expiredAt: error.expiredAt };
      }
      if (error instanceof NetworkUnavailableError) {
        return { success: false, reason: 'network' };
      }
      const message =
        error instanceof Error ? error.message : 'Erro desconhecido ao resolver registro de plugin.';
      return { success: false, reason: 'other', error: message };
    }
  }
}
